import React, { useState, useEffect } from 'react';
import Login from './components/login';
import Forge from './components/Forge';
import './App.css';
import { SettingsProvider } from './SettingsContext';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);

  // Periodic session validation (check every 30 seconds)
  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const validateSession = async () => {
      try {
        const response = await fetch('/api/usage', {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const data = await response.json();
          
          // Handle different error codes
          if (data.code === 'ACCOUNT_TERMINATED') {
            console.log('[APP] Account terminated - forcing logout');
            setSessionError('Your account has been terminated by an administrator.');
            handleLogout();
          } else if (data.code === 'ACCOUNT_REMOVED') {
            console.log('[APP] Account removed - forcing logout');
            setSessionError('Your account has been removed by an administrator.');
            handleLogout();
          } else if (data.code === 'TOKEN_EXPIRED') {
            console.log('[APP] Token expired - forcing logout');
            setSessionError('Your token has expired.');
            handleLogout();
          }
        }
      } catch (error) {
        console.error('[APP] Session validation error:', error);
        // Don't logout on network errors
      }
    };

    // Validate immediately
    validateSession();

    // Then validate every 30 seconds
    const interval = setInterval(validateSession, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, token]);

  // Initial authentication check
  useEffect(() => {
    
    const savedToken = localStorage.getItem('neuroforge_token');
    
    if (savedToken) {
      
      fetch('/api/usage', {
        headers: { 
          'Authorization': `Bearer ${savedToken}`,
          'Content-Type': 'application/json'
        }
      })
      .then(response => {
        
        if (response.ok) {
          return response.json().then(data => {
            
            // Check if account is in good standing
            if (data.usage.maxPrompts === 0) {
              console.log('[APP] Account terminated - clearing token');
              setSessionError('Your account has been terminated.');
              localStorage.removeItem('neuroforge_token');
              localStorage.removeItem('neuroforge-settings');
              setLoading(false);
              return;
            }
            
            setToken(savedToken);
            setIsAuthenticated(true);
            setLoading(false);
          });
        } else {
          return response.json().then(data => {
            console.log('[APP] Token validation failed:', data);
            
            // Handle different error codes
            if (data.code === 'ACCOUNT_TERMINATED') {
              setSessionError('Your account has been terminated by an administrator.');
            } else if (data.code === 'ACCOUNT_REMOVED') {
              setSessionError('Your account has been removed by an administrator.');
            } else if (data.code === 'TOKEN_EXPIRED') {
              setSessionError('Your token has expired.');
            }
            
            console.log('[APP] Clearing invalid token');
            localStorage.removeItem('neuroforge_token');
            localStorage.removeItem('neuroforge-settings');
            setLoading(false);
          });
        }
      })
      .catch(error => {
        console.error('[APP] Token validation failed:', error);
        localStorage.removeItem('neuroforge_token');
        localStorage.removeItem('neuroforge-settings');
        setLoading(false);
      });
    } else {
      console.log('[APP] No saved token found, showing login screen');
      setLoading(false);
    }
  }, []);

  const handleLogin = (newToken) => {
    console.log('[APP] New login with token');
    setToken(newToken);
    setIsAuthenticated(true);
    setSessionError(null);
    localStorage.setItem('neuroforge_token', newToken);
  };

  const handleLogout = () => {
    console.log('[APP] Logging out and clearing all data');
    setIsAuthenticated(false);
    setToken('');
    localStorage.removeItem('neuroforge_token');
    localStorage.removeItem('neuroforge-settings');
    
    // Don't reload immediately if there's a session error message
    if (!sessionError) {
      window.location.reload();
    }
  };

  // Global error handler for API responses
  useEffect(() => {
    const handleUnauthorized = (event) => {
      if (event.detail?.status === 401 || event.detail?.status === 403) {
        console.log('[APP] Unauthorized access detected - forcing logout');
        const errorData = event.detail?.data;
        
        if (errorData?.code === 'ACCOUNT_TERMINATED') {
          setSessionError('Your account has been terminated by an administrator.');
        } else if (errorData?.code === 'ACCOUNT_REMOVED') {
          setSessionError('Your account has been removed by an administrator.');
        } else {
          setSessionError('Your session has expired or your account access has been revoked.');
        }
        
        handleLogout();
      }
    };

    window.addEventListener('neuroforge-unauthorized', handleUnauthorized);
    return () => window.removeEventListener('neuroforge-unauthorized', handleUnauthorized);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-white text-xl">Loading NeuroForge...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      {!isAuthenticated ? (
        <>
          <Login onLogin={handleLogin} />
          {sessionError && (
            <div className="fixed bottom-4 right-4 bg-red-900/90 border border-red-600 rounded-lg p-4 max-w-md shadow-2xl">
              <div className="flex items-start space-x-3">
                <svg className="h-6 w-6 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-red-200 font-semibold text-sm">Session Terminated</h3>
                  <p className="text-red-300 text-sm mt-1">{sessionError}</p>
                  <p className="text-red-400 text-xs mt-2">Please contact support if you believe this is an error.</p>
                </div>
                <button
                  onClick={() => setSessionError(null)}
                  className="text-red-400 hover:text-red-300"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <SettingsProvider>
          <Forge token={token} onLogout={handleLogout} />
        </SettingsProvider>
      )}
    </div>
  );
}

export default App;
